/**
 * pages/AuthPage.jsx
 * Dark glassmorphism auth page with animated engineering grid,
 * drifting color blobs, floating particles, and a centered glass card.
 */

import React, { useState, useEffect, useRef } from 'react';
import API from '../services/api';

/* ─────────────────────────────────────────────────────────────────────────────
   CSS Keyframes (injected once into <head>)
───────────────────────────────────────────────────────────────────────────── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');

* { box-sizing: border-box; }

@keyframes ca-card-in {
  from { opacity: 0; transform: translateY(24px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0)    scale(1);    }
}
@keyframes ca-field-in {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes ca-blob-a {
  0%   { transform: translate(0px, 0px)     scale(1);    }
  33%  { transform: translate(40px, -30px)  scale(1.08); }
  66%  { transform: translate(-20px, 20px)  scale(0.96); }
  100% { transform: translate(0px, 0px)     scale(1);    }
}
@keyframes ca-blob-b {
  0%   { transform: translate(0px, 0px)     scale(1);    }
  33%  { transform: translate(-50px, 30px)  scale(1.1);  }
  66%  { transform: translate(30px, -20px)  scale(0.94); }
  100% { transform: translate(0px, 0px)     scale(1);    }
}
@keyframes ca-float-a {
  0%,100% { transform: translateY(0)   rotate(0deg);   }
  50%     { transform: translateY(-16px) rotate(8deg);  }
}
@keyframes ca-float-b {
  0%,100% { transform: translateY(0)   rotate(0deg);   }
  50%     { transform: translateY(-11px) rotate(-10deg); }
}
@keyframes ca-float-c {
  0%,100% { transform: translateY(0)   rotate(0deg);   }
  50%     { transform: translateY(-20px) rotate(5deg);  }
}
@keyframes ca-float-d {
  0%,100% { transform: translateY(0)   rotate(0deg);   }
  50%     { transform: translateY(-9px) rotate(-6deg);  }
}
@keyframes ca-spin {
  to { transform: rotate(360deg); }
}
@keyframes ca-shake {
  0%,100% { transform: translateX(0); }
  18%     { transform: translateX(-8px); }
  36%     { transform: translateX(8px);  }
  54%     { transform: translateX(-6px); }
  72%     { transform: translateX(6px);  }
}
@keyframes ca-shimmer {
  0%   { left: -100%; opacity: 0.6; }
  60%  { opacity: 0.6; }
  100% { left: 160%;  opacity: 0; }
}
@keyframes ca-bar-in {
  from { transform: scaleX(0); opacity: 0; }
  to   { transform: scaleX(1); opacity: 1; }
}
@keyframes ca-scan-line {
  0%   { top: 0%;    opacity: 0.8; }
  90%  { top: 100%;  opacity: 0.4; }
  100% { top: 0%;    opacity: 0.8; }
}

/* Input placeholder colour in dark inputs */
.ca-input::placeholder { color: rgba(255,255,255,0.28); }
.ca-input:focus { outline: none; }
.ca-input { caret-color: #F97316; }

/* Scrollbar for overflow card */
.ca-scroll::-webkit-scrollbar { width: 4px; }
.ca-scroll::-webkit-scrollbar-track { background: transparent; }
.ca-scroll::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.12); border-radius: 4px; }
`;

let _injected = false;
function injectCSS() {
  if (_injected) return;
  const el = document.createElement('style');
  el.textContent = CSS;
  document.head.appendChild(el);
  _injected = true;
}

/* ─────────────────────────────────────────────────────────────────────────────
   Role config
───────────────────────────────────────────────────────────────────────────── */
const ROLES = {
  uploader: {
    label:      'Uploader',
    tagline:    'Upload & scan engineering drawings.',
    accent:     '#F97316',
    accentB:    '#FB923C',
    accentDark: '#C2410C',
    blobA:      'rgba(249,115,22,0.28)',
    blobB:      'rgba(194,65,12,0.20)',
    bar:        'linear-gradient(90deg, #F97316, #FB923C, #F97316)',
    loginSub:   'Sign in to start scanning drawings.',
    regSub:     'Create an account to start scanning.',
  },
  reviewer: {
    label:      'Reviewer',
    tagline:    'Review & approve inspection plans.',
    accent:     '#10B981',
    accentB:    '#34D399',
    accentDark: '#059669',
    blobA:      'rgba(16,185,129,0.25)',
    blobB:      'rgba(5,150,105,0.18)',
    bar:        'linear-gradient(90deg, #10B981, #34D399, #10B981)',
    loginSub:   'Sign in to review inspection plans.',
    regSub:     'Create a Reviewer account.',
  },
};

/* ─────────────────────────────────────────────────────────────────────────────
   Background floating particles (engineering micro-icons)
───────────────────────────────────────────────────────────────────────────── */
const PARTICLES = [
  { id:'p1', top:'8%',  left:'6%',  size:38, anim:'ca-float-a', dur:'6.2s', delay:'0s',    op:0.09 },
  { id:'p2', top:'12%', right:'7%', size:32, anim:'ca-float-b', dur:'7.8s', delay:'1.2s',  op:0.08 },
  { id:'p3', top:'38%', left:'3%',  size:28, anim:'ca-float-c', dur:'5.5s', delay:'0.5s',  op:0.07 },
  { id:'p4', top:'62%', right:'4%', size:34, anim:'ca-float-d', dur:'8.4s', delay:'2.1s',  op:0.09 },
  { id:'p5', top:'78%', left:'8%',  size:30, anim:'ca-float-a', dur:'6.9s', delay:'1.8s',  op:0.07 },
  { id:'p6', top:'85%', right:'9%', size:36, anim:'ca-float-b', dur:'7.2s', delay:'0.9s',  op:0.08 },
  { id:'p7', top:'48%', left:'88%', size:26, anim:'ca-float-c', dur:'9.1s', delay:'3.0s',  op:0.06 },
  { id:'p8', top:'28%', right:'2%', size:30, anim:'ca-float-d', dur:'6.6s', delay:'1.5s',  op:0.07 },
];

function ParticleIcon({ idx }) {
  const icons = [
    /* CC Diamond */
    <svg viewBox="0 0 32 32" fill="none">
      <polygon points="16,2 30,16 16,30 2,16" stroke="white" strokeWidth="1.4" fill="white" fillOpacity="0.12"/>
      <text x="16" y="20" textAnchor="middle" fill="white" fontSize="8" fontWeight="800" fontFamily="Inter,sans-serif">CC</text>
    </svg>,
    /* Ruler */
    <svg viewBox="0 0 38 14" fill="none">
      <rect x="0.5" y="0.5" width="37" height="13" rx="2.5" stroke="white" strokeWidth="1.2"/>
      {[5,10,15,20,25,30,35].map((x,i)=>(
        <line key={x} x1={x} y1="13" x2={x} y2={i%2===0?5:8} stroke="white" strokeWidth="0.9"/>
      ))}
    </svg>,
    /* Angle */
    <svg viewBox="0 0 30 30" fill="none">
      <path d="M4 26 L4 4 L26 26 Z" stroke="white" strokeWidth="1.4" fill="none"/>
      <path d="M4 20 A16 16 0 0 1 12 26" stroke="white" strokeWidth="1.1" fill="none"/>
      <text x="11" y="24" fill="white" fontSize="5.5" fontFamily="Inter,sans-serif">45°</text>
    </svg>,
    /* Gear */
    <svg viewBox="0 0 34 34" fill="none">
      <circle cx="17" cy="17" r="5.5" stroke="white" strokeWidth="1.4" fill="none"/>
      <circle cx="17" cy="17" r="2" fill="white" fillOpacity="0.5"/>
      {[0,45,90,135,180,225,270,315].map(a=>{
        const rad=Math.PI*a/180;
        return <line key={a} x1={17+8*Math.cos(rad)} y1={17+8*Math.sin(rad)} x2={17+13.5*Math.cos(rad)} y2={17+13.5*Math.sin(rad)} stroke="white" strokeWidth="2.8" strokeLinecap="round"/>;
      })}
    </svg>,
    /* Circle target */
    <svg viewBox="0 0 28 28" fill="none">
      <circle cx="14" cy="14" r="12" stroke="white" strokeWidth="1.2"/>
      <circle cx="14" cy="14" r="7"  stroke="white" strokeWidth="1"/>
      <circle cx="14" cy="14" r="2.5" fill="white" fillOpacity="0.6"/>
      <line x1="14" y1="2" x2="14" y2="6" stroke="white" strokeWidth="1.2"/>
      <line x1="14" y1="22" x2="14" y2="26" stroke="white" strokeWidth="1.2"/>
      <line x1="2" y1="14" x2="6" y2="14" stroke="white" strokeWidth="1.2"/>
      <line x1="22" y1="14" x2="26" y2="14" stroke="white" strokeWidth="1.2"/>
    </svg>,
    /* Hex bolt */
    <svg viewBox="0 0 30 30" fill="none">
      <polygon points="15,2 26,8 26,22 15,28 4,22 4,8" stroke="white" strokeWidth="1.3" fill="white" fillOpacity="0.08"/>
      <circle cx="15" cy="15" r="5" stroke="white" strokeWidth="1.2" fill="none"/>
    </svg>,
    /* Arrow dimension */
    <svg viewBox="0 0 34 18" fill="none">
      <line x1="2" y1="9" x2="32" y2="9" stroke="white" strokeWidth="1.2"/>
      <polyline points="6,4 2,9 6,14"  stroke="white" strokeWidth="1.2" fill="none" strokeLinejoin="round"/>
      <polyline points="28,4 32,9 28,14" stroke="white" strokeWidth="1.2" fill="none" strokeLinejoin="round"/>
      <line x1="17" y1="2" x2="17" y2="16" stroke="white" strokeWidth="0.8" strokeDasharray="2 2"/>
    </svg>,
    /* Scan crosshair */
    <svg viewBox="0 0 30 30" fill="none">
      <path d="M2 8 L2 2 L8 2" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      <path d="M22 2 L28 2 L28 8" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      <path d="M28 22 L28 28 L22 28" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      <path d="M8 28 L2 28 L2 22" stroke="white" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      <circle cx="15" cy="15" r="4" stroke="white" strokeWidth="1.1" fill="none"/>
    </svg>,
  ];
  return icons[idx % icons.length];
}

/* ─────────────────────────────────────────────────────────────────────────────
   Eye icon
───────────────────────────────────────────────────────────────────────────── */
function EyeIcon({ open }) {
  return open ? (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  ) : (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Reusable dark input
───────────────────────────────────────────────────────────────────────────── */
function DarkInput({ field, label, type='text', placeholder, value, onChange, onFocus, onBlur,
                     focusedField, accent, error, extra, autoFocus, autoComplete, children }) {
  const focused = focusedField === field;
  return (
    <div style={{ marginBottom: 0 }}>
      {label && (
        <label style={{
          display: 'block', fontSize: 12, fontWeight: 600,
          color: focused ? accent : 'rgba(255,255,255,0.55)',
          marginBottom: 6, letterSpacing: '0.02em',
          transition: 'color 0.2s',
        }}>
          {label}
        </label>
      )}
      <div style={{ position: 'relative' }}>
        <input
          className="ca-input"
          type={type}
          placeholder={placeholder}
          value={value}
          autoFocus={autoFocus}
          autoComplete={autoComplete}
          onFocus={onFocus}
          onBlur={onBlur}
          onChange={onChange}
          style={{
            width: '100%', padding: children ? '11px 42px 11px 14px' : '11px 14px',
            background: focused
              ? 'rgba(255,255,255,0.08)'
              : error ? 'rgba(220,38,38,0.08)' : 'rgba(255,255,255,0.05)',
            border: `1.5px solid ${error ? 'rgba(239,68,68,0.6)' : focused ? accent : 'rgba(255,255,255,0.10)'}`,
            borderRadius: 12,
            fontSize: 14, color: '#F1F5F9',
            transition: 'background 0.2s, border-color 0.2s, box-shadow 0.2s',
            boxShadow: focused ? `0 0 0 3px ${accent}28, 0 0 18px ${accent}14` : 'none',
            fontFamily: "'Inter','Segoe UI',sans-serif",
          }}
          {...extra}
        />
        {children && (
          <div style={{
            position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
          }}>
            {children}
          </div>
        )}
      </div>
      {error && (
        <span style={{
          display: 'block', fontSize: 11, color: '#F87171', marginTop: 4,
          animation: 'ca-field-in 0.2s ease',
        }}>
          {error}
        </span>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
   Main component
───────────────────────────────────────────────────────────────────────────── */
export default function AuthPage({ onAuthSuccess, onBack, initialRole = 'uploader' }) {
  injectCSS();

  const [role,        setRole]        = useState(initialRole);
  const [mode,        setMode]        = useState('login');
  const [username,    setUsername]    = useState('');
  const [email,       setEmail]       = useState('');
  const [password,    setPassword]    = useState('');
  const [showPass,    setShowPass]    = useState(false);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [mounted,     setMounted]     = useState(false);
  const [focusedField, setFocused]    = useState(null);
  const [shakeKey,    setShakeKey]    = useState(0);
  const [barKey,      setBarKey]      = useState(0);
  const [hoverBtn,    setHoverBtn]    = useState(false);

  const isLogin = mode === 'login';
  const cfg     = ROLES[role];
  const roleIdx = role === 'uploader' ? 0 : 1;

  useEffect(() => { requestAnimationFrame(() => setMounted(true)); }, []);

  const switchRole = (r) => {
    if (r === role) return;
    setRole(r);
    setBarKey(k => k + 1);
    setError('');
    setFieldErrors({});
  };

  const switchMode = (m) => {
    setMode(m);
    setError('');
    setFieldErrors({});
    setPassword('');
  };

  const validate = () => {
    const e = {};
    if (!username.trim())                e.username = 'Username is required.';
    else if (username.trim().length < 3) e.username = 'At least 3 characters.';
    if (!isLogin) {
      if (!email.trim())                     e.email = 'Email is required.';
      else if (!/\S+@\S+\.\S+/.test(email))  e.email = 'Enter a valid email.';
    }
    if (!password)               e.password = 'Password is required.';
    else if (password.length < 6) e.password = 'At least 6 characters.';
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setError('');
    const errs = validate();
    setFieldErrors(errs);
    if (Object.keys(errs).length) { setShakeKey(k => k + 1); return; }
    setLoading(true);
    try {
      const data = isLogin
        ? await API.login(username.trim(), password)
        : await API.register(username.trim(), email.trim(), password, role);
      localStorage.setItem('auth_token',    data.access_token);
      localStorage.setItem('auth_username', data.username);
      localStorage.setItem('auth_email',    data.email);
      localStorage.setItem('auth_role',     data.role || role);
      onAuthSuccess(data);
    } catch (err) {
      setError(err.error || 'Something went wrong. Please try again.');
      setShakeKey(k => k + 1);
    } finally {
      setLoading(false);
    }
  };

  return (
    /* ── Viewport ── */
    <div style={{
      position: 'fixed', inset: 0,
      background: '#080C14',
      backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.032) 1px, transparent 1px)',
      backgroundSize: '28px 28px',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Inter','Segoe UI',sans-serif",
      overflow: 'hidden',
    }}>

      {/* ── Blob A (top-left) ── */}
      <div style={{
        position: 'absolute', top: '-10%', left: '-8%',
        width: 520, height: 520, borderRadius: '50%',
        background: cfg.blobA,
        filter: 'blur(90px)',
        animation: 'ca-blob-a 18s ease-in-out infinite',
        transition: 'background 0.8s ease',
        pointerEvents: 'none',
      }}/>

      {/* ── Blob B (bottom-right) ── */}
      <div style={{
        position: 'absolute', bottom: '-12%', right: '-6%',
        width: 600, height: 600, borderRadius: '50%',
        background: cfg.blobB,
        filter: 'blur(100px)',
        animation: 'ca-blob-b 22s ease-in-out infinite',
        transition: 'background 0.8s ease',
        pointerEvents: 'none',
      }}/>

      {/* ── Blob C (subtle center) ── */}
      <div style={{
        position: 'absolute', top: '35%', left: '38%',
        width: 300, height: 300, borderRadius: '50%',
        background: `${cfg.accent}0A`,
        filter: 'blur(70px)',
        transition: 'background 0.8s ease',
        pointerEvents: 'none',
      }}/>

      {/* ── Floating particles ── */}
      {PARTICLES.map((p, i) => (
        <div key={p.id} style={{
          position: 'absolute',
          top: p.top, left: p.left, right: p.right, bottom: p.bottom,
          width: p.size, height: p.size,
          opacity: p.op,
          animation: `${p.anim} ${p.dur} ${p.delay} ease-in-out infinite`,
          pointerEvents: 'none',
        }}>
          <ParticleIcon idx={i} />
        </div>
      ))}

      {/* ── Glass Card ── */}
      <div style={{
        position: 'relative',
        width: '100%', maxWidth: 440,
        margin: '0 16px',
        opacity: mounted ? 1 : 0,
        animation: mounted ? 'ca-card-in 0.55s cubic-bezier(0.16,1,0.3,1) both' : 'none',
        zIndex: 10,
      }}>

        {/* Accent top bar with shimmer */}
        <div key={barKey} style={{
          height: 4, borderRadius: '16px 16px 0 0',
          background: cfg.bar,
          backgroundSize: '200% 100%',
          position: 'relative', overflow: 'hidden',
          animation: 'ca-bar-in 0.4s ease',
          transition: 'background 0.5s ease',
        }}>
          {/* Shimmer sweep */}
          <div style={{
            position: 'absolute', top: 0, bottom: 0, width: '50%',
            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent)',
            animation: 'ca-shimmer 0.7s ease 0.1s both',
          }}/>
        </div>

        {/* Card body */}
        <div
          className="ca-scroll"
          style={{
            background: 'rgba(10,15,25,0.78)',
            backdropFilter: 'blur(28px) saturate(1.5)',
            WebkitBackdropFilter: 'blur(28px) saturate(1.5)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderTop: 'none',
            borderRadius: '0 0 16px 16px',
            boxShadow: '0 32px 80px rgba(0,0,0,0.65), inset 0 1px 0 rgba(255,255,255,0.05)',
            padding: '28px 32px 30px',
            maxHeight: 'calc(100vh - 48px)',
            overflowY: 'auto',
          }}
        >

          {/* ── Header row: logo + back ── */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {/* Logo icon */}
              <div style={{
                width: 36, height: 36, borderRadius: 10,
                background: `linear-gradient(135deg, ${cfg.accent}, ${cfg.accentDark})`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: `0 4px 14px ${cfg.accent}50`,
                transition: 'background 0.4s, box-shadow 0.4s',
                flexShrink: 0,
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                  stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18"/>
                </svg>
              </div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: '#F1F5F9', letterSpacing: '-0.2px', lineHeight: 1.2 }}>
                  InSpectly<span style={{ color: cfg.accent, transition: 'color 0.3s' }}>.AI</span>
                </div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', marginTop: 1 }}>
                  Engineering Intelligence
                </div>
              </div>
            </div>

            {onBack && (
              <button onClick={onBack} style={{
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.10)',
                borderRadius: 8, cursor: 'pointer',
                color: 'rgba(255,255,255,0.5)', fontSize: 12,
                fontFamily: 'inherit', padding: '5px 11px',
                transition: 'all 0.15s',
                display: 'inline-flex', alignItems: 'center', gap: 4,
              }}
                onMouseEnter={e => { e.currentTarget.style.background='rgba(255,255,255,0.12)'; e.currentTarget.style.color='rgba(255,255,255,0.85)'; }}
                onMouseLeave={e => { e.currentTarget.style.background='rgba(255,255,255,0.06)'; e.currentTarget.style.color='rgba(255,255,255,0.5)'; }}
              >
                ← Back
              </button>
            )}
          </div>

          {/* ── Role pill toggle ── */}
          <div style={{
            position: 'relative', display: 'flex',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.07)',
            borderRadius: 12, padding: 3,
            marginBottom: 26,
          }}>
            {/* Sliding highlight pill */}
            <div style={{
              position: 'absolute',
              top: 3,
              left: roleIdx === 0 ? 3 : 'calc(50% + 1.5px)',
              width: 'calc(50% - 4.5px)',
              height: 'calc(100% - 6px)',
              borderRadius: 9,
              background: 'rgba(255,255,255,0.08)',
              border: `1px solid ${cfg.accent}40`,
              boxShadow: `0 0 12px ${cfg.accent}20`,
              transition: 'left 0.28s cubic-bezier(.4,0,.2,1), border-color 0.4s, box-shadow 0.4s',
            }}/>
            {['uploader','reviewer'].map((r,i) => {
              const rc = ROLES[r];
              const active = role === r;
              return (
                <button key={r} type="button" onClick={() => switchRole(r)}
                  style={{
                    flex: 1, position: 'relative', zIndex: 1,
                    padding: '8px 0', border: 'none', background: 'none',
                    borderRadius: 9, cursor: 'pointer',
                    fontSize: 13, fontWeight: active ? 700 : 400,
                    color: active ? rc.accent : 'rgba(255,255,255,0.35)',
                    transition: 'color 0.28s, font-weight 0.1s',
                    fontFamily: 'inherit', letterSpacing: active ? '-0.1px' : '0',
                  }}
                >
                  {rc.label}
                </button>
              );
            })}
          </div>

          {/* ── Heading ── */}
          <div style={{ marginBottom: 22 }}>
            <h1 style={{
              fontSize: 22, fontWeight: 800, color: '#F1F5F9',
              margin: '0 0 5px', letterSpacing: '-0.4px', lineHeight: 1.2,
            }}>
              {isLogin ? 'Welcome back' : 'Create account'}
            </h1>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.42)', margin: 0, lineHeight: 1.5 }}>
              {isLogin ? cfg.loginSub : cfg.regSub}
            </p>
          </div>

          {/* ── Error banner ── */}
          {error && (
            <div key={`err-${shakeKey}`} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              background: 'rgba(239,68,68,0.12)',
              border: '1px solid rgba(239,68,68,0.35)',
              borderRadius: 10, padding: '10px 13px',
              color: '#FCA5A5', fontSize: 13, marginBottom: 18,
              animation: 'ca-shake 0.4s ease',
            }}>
              <div style={{
                width: 20, height: 20, borderRadius: '50%',
                background: 'rgba(239,68,68,0.3)', border: '1px solid rgba(239,68,68,0.5)',
                color: '#FCA5A5', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, fontWeight: 700, flexShrink: 0,
              }}>!</div>
              {error}
            </div>
          )}

          {/* ── Form ── */}
          <form onSubmit={handleSubmit} noValidate>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

              {/* Username */}
              <div style={{ animation: 'ca-field-in 0.4s 0.05s ease both' }}>
                <DarkInput
                  field="username" label="Username" type="text"
                  placeholder="your_username"
                  value={username} autoFocus autoComplete="username"
                  focusedField={focusedField} accent={cfg.accent}
                  error={fieldErrors.username}
                  onFocus={() => setFocused('username')}
                  onBlur={() => setFocused(null)}
                  onChange={e => { setUsername(e.target.value); setFieldErrors(p=>({...p,username:''})); }}
                />
              </div>

              {/* Email (register only) */}
              <div style={{
                overflow: 'hidden',
                maxHeight: !isLogin ? 80 : 0,
                opacity: !isLogin ? 1 : 0,
                transition: 'max-height 0.32s ease, opacity 0.25s ease',
              }}>
                <DarkInput
                  field="email" label="Email" type="email"
                  placeholder="you@company.com"
                  value={email} autoComplete="email"
                  focusedField={focusedField} accent={cfg.accent}
                  error={fieldErrors.email}
                  onFocus={() => setFocused('email')}
                  onBlur={() => setFocused(null)}
                  onChange={e => { setEmail(e.target.value); setFieldErrors(p=>({...p,email:''})); }}
                />
              </div>

              {/* Password */}
              <div style={{ animation: 'ca-field-in 0.4s 0.10s ease both' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                  <label style={{
                    fontSize: 12, fontWeight: 600,
                    color: focusedField === 'password' ? cfg.accent : 'rgba(255,255,255,0.55)',
                    transition: 'color 0.2s', letterSpacing: '0.02em',
                  }}>Password</label>
                  {isLogin && (
                    <span style={{
                      fontSize: 11, color: cfg.accent, cursor: 'pointer', fontWeight: 500,
                      opacity: 0.75, transition: 'opacity 0.15s',
                    }}
                      onMouseEnter={e => e.currentTarget.style.opacity='1'}
                      onMouseLeave={e => e.currentTarget.style.opacity='0.75'}
                    >
                      Forgot password?
                    </span>
                  )}
                </div>
                <DarkInput
                  field="password" label={null}
                  type={showPass ? 'text' : 'password'}
                  placeholder={isLogin ? '••••••••' : 'Min 6 characters'}
                  value={password} autoComplete={isLogin ? 'current-password' : 'new-password'}
                  focusedField={focusedField} accent={cfg.accent}
                  error={fieldErrors.password}
                  onFocus={() => setFocused('password')}
                  onBlur={() => setFocused(null)}
                  onChange={e => { setPassword(e.target.value); setFieldErrors(p=>({...p,password:''})); }}
                >
                  <button type="button" onClick={() => setShowPass(v => !v)} style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: 'rgba(255,255,255,0.6)', padding: 2, display: 'flex',
                    transition: 'color 0.15s',
                  }}
                    onMouseEnter={e => e.currentTarget.style.color='rgba(255,255,255,0.7)'}
                    onMouseLeave={e => e.currentTarget.style.color='rgba(255,255,255,0.6)'}
                  >
                    <EyeIcon open={showPass} />
                  </button>
                </DarkInput>
              </div>

            </div>

            {/* ── Submit button ── */}
            <button
              type="submit"
              disabled={loading}
              onMouseEnter={() => setHoverBtn(true)}
              onMouseLeave={() => setHoverBtn(false)}
              style={{
                width: '100%', marginTop: 22, padding: '13px 0',
                border: 'none', borderRadius: 12,
                background: loading
                  ? 'rgba(255,255,255,0.08)'
                  : `linear-gradient(135deg, ${cfg.accent} 0%, ${cfg.accentDark} 100%)`,
                color: loading ? 'rgba(255,255,255,0.4)' : '#fff',
                fontSize: 14, fontWeight: 700,
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'transform 0.18s, box-shadow 0.18s, background 0.3s',
                transform: hoverBtn && !loading ? 'translateY(-2px)' : 'translateY(0)',
                boxShadow: hoverBtn && !loading
                  ? `0 8px 28px ${cfg.accent}55, 0 2px 8px rgba(0,0,0,0.4)`
                  : `0 4px 14px ${cfg.accent}30, 0 2px 4px rgba(0,0,0,0.3)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                minHeight: 48, fontFamily: 'inherit', letterSpacing: '0.01em',
              }}
            >
              {loading ? (
                <span style={{
                  width: 18, height: 18,
                  border: '2.5px solid rgba(255,255,255,0.2)',
                  borderTopColor: 'rgba(255,255,255,0.7)',
                  borderRadius: '50%',
                  animation: 'ca-spin 0.7s linear infinite',
                  display: 'inline-block',
                }}/>
              ) : (
                isLogin ? 'Sign In' : 'Create Account'
              )}
            </button>
          </form>

          {/* ── Divider ── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
            <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.07)' }}/>
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', fontWeight: 500, letterSpacing: '0.05em' }}>
              OR
            </span>
            <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.07)' }}/>
          </div>

          {/* No public signup — accounts are provisioned after a Book Meeting
              conversation (global lead-form policy). Login only. */}
          <p style={{ textAlign: 'center', margin: 0, fontSize: 13, color: 'rgba(255,255,255,0.38)' }}>
            Need an account? Book a meeting from the homepage and we'll set you up.
          </p>

        </div>
      </div>
    </div>
  );
}
